import {
	createContext,
	use,
	useCallback,
	useMemo,
	useState,
	type ComponentPropsWithRef,
	type ComponentType,
	type ReactNode,
} from 'react';

import { useLingui } from '@lingui/react/macro';
import { Link, type LinkProps } from '@tanstack/react-router';
import { Lock, Menu, PanelLeftClose, PanelLeftOpen } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { useIsMobile } from '@/hooks/use-mobile';
import { useHasAnyPermission } from '@/hooks/usePermissions';
import type { PermissionKey } from '@/lib/services/permissions.service';
import { isBooleanValue, readVersioned, writeVersioned } from '@/lib/storage/versioned-storage';
import { cn } from '@/lib/utils';

export interface AreaHue {
	h: number;
	c: number;
}

// M3 color roles over per-item --h/--c vars: container tone 90 light / 30 dark, on-container inverse.
export const INDICATOR_BG = 'bg-[oklch(0.90_var(--c)_var(--h))] dark:bg-[oklch(0.38_var(--c)_var(--h))]';
export const ON_INDICATOR = 'text-[oklch(0.34_calc(var(--c)*1.5)_var(--h))] dark:text-[oklch(0.92_var(--c)_var(--h))]';
/**
 * Topbar row height, declared once on whatever wraps a rail + chrome pair. Exported with the two
 * gradients below so the boot shell skeleton paints the same chrome, at the same dimensions, as the
 * real shell it is replaced by - the swap is then a repaint rather than a layout shift.
 */
export const SHELL_HEADER_HEIGHT = '[--header-height:3.5rem]';

// The ramps invert per colour scheme, so the rail's fixed navy and greys name a different step in dark mode.
export const CHROME_SURFACE = 'bg-(--surface)';
export const RAIL_SURFACE = 'bg-(--ramp-deep-navy-fg-3) dark:bg-(--ramp-deep-navy-bg-4)';

// Frame classes shared with `AppShellSkeleton` for the same reason as the gradients above: the
// skeleton must occupy exactly the space the real shell will, so a change here reshapes both.
export const SHELL_RAIL_FRAME = 'w-20 shrink-0 flex-col items-stretch gap-5 pt-4';
export const SHELL_TOPBAR_FRAME = 'flex h-(--header-height) w-full items-center px-2 md:px-4';
export const SHELL_CONTENT_FRAME =
	'mb-3 mr-2 ml-2 md:ml-0 flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl bg-background';

const RAIL_DISC_IDLE =
	'border border-(--fg-5) text-white group-hover:bg-(--fg-4) dark:border-(--bg-5) dark:group-hover:bg-(--bg-4)';
const RAIL_DISC_ACTIVE =
	'bg-primary text-primary-foreground drop-shadow-[0_4px_16px_rgba(2,6,23,0.5)] shadow-[inset_0_1px_14.2px_0_rgba(119,212,255,0.5),inset_0_6px_12px_0_rgba(255,255,255,0.25)]';

interface RailShellState {
	panelOpen: boolean;
	mobileOpen: boolean;
	isMobile: boolean;
	hasPanel: boolean;
}

interface RailShellActions {
	setPanelOpen: (open: boolean) => void;
	togglePanel: () => void;
	setMobileOpen: (open: boolean) => void;
}

interface RailShellContextValue {
	state: RailShellState;
	actions: RailShellActions;
}

const RailShellContext = createContext<RailShellContextValue | null>(null);

export function useRailShell(): RailShellContextValue {
	const ctx = use(RailShellContext);
	if (!ctx) throw new Error('useRailShell must be used within <RailShell>');
	return ctx;
}

// Null outside the provider so shared page headers can render the toggle safely.
export function useOptionalRailShell(): RailShellContextValue | null {
	return use(RailShellContext);
}

const readPersistedPanelOpen = (key: string | undefined) => {
	if (!key) return true;
	return readVersioned({ key, isValid: isBooleanValue }) ?? true;
};

interface RailShellProps {
	children: ReactNode;
	persistKey?: string;
	className?: string;
	hasPanel?: boolean;
}

function RailShellRoot({ children, persistKey, className, hasPanel = false }: RailShellProps) {
	const isMobile = useIsMobile();
	const [panelOpen, setPanelOpenState] = useState(() => readPersistedPanelOpen(persistKey));
	const [mobileOpen, setMobileOpen] = useState(false);

	const setPanelOpen = useCallback(
		(open: boolean) => {
			setPanelOpenState(open);
			if (!persistKey) return;
			writeVersioned({ key: persistKey, data: open });
		},
		[persistKey],
	);

	const value = useMemo<RailShellContextValue>(
		() => ({
			state: { panelOpen, mobileOpen, isMobile, hasPanel },
			actions: {
				setPanelOpen,
				togglePanel: () => setPanelOpen(!panelOpen),
				setMobileOpen,
			},
		}),
		[panelOpen, mobileOpen, isMobile, hasPanel, setPanelOpen],
	);

	return (
		<RailShellContext.Provider value={value}>
			<div className={cn('flex h-dvh', SHELL_HEADER_HEIGHT, className)}>{children}</div>
		</RailShellContext.Provider>
	);
}

interface RailProps {
	children: ReactNode;
	logo?: ReactNode;
	className?: string;
}

function Rail({ children, logo, className }: RailProps) {
	const { t } = useLingui();
	return (
		<nav
			className={cn('flex overflow-y-auto scrollbar-thin', SHELL_RAIL_FRAME, RAIL_SURFACE, className)}
			aria-label={t`Areas`}
		>
			{logo ? <div className="flex shrink-0 items-center justify-center pb-1.5">{logo}</div> : null}
			{children}
		</nav>
	);
}

export interface RailItemIconProps {
	className?: string;
}

interface RailItemProps extends ComponentPropsWithRef<'button'> {
	icon: ComponentType<RailItemIconProps>;
	label: string;
	caption?: string;
	active?: boolean;
	onSelect?: () => void;
}

// Icon disc over an always-visible caption. Extra button props pass through so composition slots
// (e.g. PopoverTrigger render) can drive it.
function RailItem({ icon: Icon, label, caption, active, disabled, onSelect, className, ...rest }: RailItemProps) {
	return (
		<button
			type="button"
			{...rest}
			onClick={(event) => {
				rest.onClick?.(event);
				onSelect?.();
			}}
			disabled={disabled}
			aria-label={label}
			aria-current={active ? 'true' : undefined}
			className={cn(
				'group relative flex w-full shrink-0 cursor-pointer flex-col items-center gap-1 outline-none transition-opacity duration-200 disabled:pointer-events-none disabled:opacity-50',
				!active && 'opacity-70 hover:opacity-100 focus-visible:opacity-100',
				className,
			)}
		>
			<span
				className={cn(
					'flex size-10 items-center justify-center rounded-full transition-colors duration-200 group-focus-visible:ring-2 group-focus-visible:ring-white/70',
					active ? RAIL_DISC_ACTIVE : RAIL_DISC_IDLE,
				)}
			>
				<Icon className="size-5" />
			</span>
			<span className={cn('w-full px-1 text-center text-xs text-white', active && 'font-semibold')}>
				{caption ?? label}
			</span>
		</button>
	);
}

// Panel and topbar share one flat surface; content paints its own background over it.
function Chrome({ children, className }: { children: ReactNode; className?: string }) {
	return <div className={cn('flex min-w-0 flex-1 flex-col', CHROME_SURFACE, className)}>{children}</div>;
}

function Body({ children }: { children: ReactNode }) {
	return <div className="flex min-h-0 flex-1">{children}</div>;
}

// The column right of a full-height panel: topbar over content.
function Main({ children }: { children: ReactNode }) {
	return <div className="flex min-w-0 flex-1 flex-col md:pr-2">{children}</div>;
}

interface PanelProps {
	children: ReactNode;
	header?: ReactNode;
	hidden?: boolean;
	'data-testid'?: string;
}

// Stays mounted inside a width-transitioning wrapper so collapse/expand animates.
// The panel is header-less; the open/collapse control lives in the content header.
function Panel({ children, header, hidden, 'data-testid': testId }: PanelProps) {
	const { state } = useRailShell();
	const open = state.panelOpen && !hidden;
	return (
		<div
			className={cn(
				'hidden shrink-0 overflow-hidden transition-[width] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] md:block',
				open ? 'w-53' : 'w-0',
			)}
			aria-hidden={!open}
			inert={!open}
		>
			<div className="flex h-full w-53 flex-col" data-testid={testId}>
				{header && open && !state.isMobile ? (
					<div className="flex h-15 shrink-0 items-center px-3.5">{header}</div>
				) : null}
				<div className="min-h-0 flex-1 overflow-y-auto scrollbar-thin">{children}</div>
			</div>
		</div>
	);
}

// Content floats as a rounded card on the chrome surface.
function Content({ children, className }: { children: ReactNode; className?: string }) {
	return <main className={cn(SHELL_CONTENT_FRAME, className)}>{children}</main>;
}

// Open/collapse control for the section panel; rendered at the left of the content
// header. Returns null when the active area has no panel. Safe outside the shell.
function ContentPanelToggle({ className }: { className?: string }) {
	const ctx = useOptionalRailShell();
	if (!ctx || !ctx.state.hasPanel) return null;
	const { state, actions } = ctx;
	return (
		<Button
			variant="ghost"
			size="icon"
			className={cn('hidden size-7 shrink-0 text-muted-foreground md:inline-flex', className)}
			onClick={actions.togglePanel}
			aria-label={state.panelOpen ? 'Hide panel' : 'Show panel'}
			data-testid="content-panel-toggle"
		>
			{state.panelOpen ? <PanelLeftClose className="size-4" /> : <PanelLeftOpen className="size-4" />}
		</Button>
	);
}

function MobileTrigger({ className }: { className?: string }) {
	const { t } = useLingui();
	const { actions } = useRailShell();
	return (
		<Button
			variant="ghost"
			size="icon-sm"
			className={cn('mr-1 md:hidden', className)}
			onClick={() => actions.setMobileOpen(true)}
			aria-label={t`Open menu`}
			data-testid="shell-mobile-trigger"
		>
			<Menu className="size-4" />
		</Button>
	);
}

// Rail + panel side by side inside a left sheet; caller closes it on navigation.
function MobileSheet({ children }: { children: ReactNode }) {
	const { state, actions } = useRailShell();
	if (!state.isMobile) return null;
	return (
		<Sheet open={state.mobileOpen} onOpenChange={actions.setMobileOpen}>
			<SheetContent
				side="left"
				className="p-0 data-[side=left]:w-auto data-[side=left]:max-w-[86vw]"
				showCloseButton={false}
			>
				<div className={cn('flex h-full', SHELL_HEADER_HEIGHT)}>{children}</div>
			</SheetContent>
		</Sheet>
	);
}

export const RailShell = Object.assign(RailShellRoot, {
	Rail,
	RailItem,
	Chrome,
	Body,
	Main,
	Panel,
	Content,
	ContentPanelToggle,
	MobileTrigger,
	MobileSheet,
});

type PanelLinkTo = LinkProps['to'];

interface ShellPanelGroupProps {
	label?: ReactNode;
	children: ReactNode;
	// Hides the whole group (label + items) unless the user holds at least one of these. Omit to always show.
	anyPermission?: readonly PermissionKey[];
	'data-testid'?: string;
}

export function ShellPanelGroup({ label, children, anyPermission, 'data-testid': testId }: ShellPanelGroupProps) {
	const allowed = useHasAnyPermission(anyPermission ?? []);
	if (anyPermission && anyPermission.length > 0 && !allowed) return null;
	return (
		<div className="flex flex-col px-2 pb-3" data-testid={testId}>
			{label ? (
				<span className="flex h-8 shrink-0 items-center px-2 text-sm font-medium text-sidebar-foreground">{label}</span>
			) : null}
			{children}
		</div>
	);
}

const PANEL_ITEM_BASE =
	'flex h-8 w-full shrink-0 items-center gap-2 rounded-md p-2 text-sm text-sidebar-foreground transition-colors hover:bg-foreground/5 aria-disabled:pointer-events-none aria-disabled:opacity-50';

// A row whose destination sells an add-on the tenant does not hold. It stays clickable, but recedes
// so the rows that actually work read as the live ones.
const PANEL_ITEM_LOCKED = 'text-muted-foreground/55 [&_svg]:opacity-60 hover:text-muted-foreground';

// The fill is a background image because TanStack joins activeProps without merging, so a plain
// background colour would lose to the base hover.
const PANEL_ITEM_ACTIVE =
	'bg-linear-to-b from-[oklch(from_var(--color-primary)_0.885_calc(c*0.28)_h)] to-[oklch(from_var(--color-primary)_0.885_calc(c*0.28)_h)] font-medium data-[active=true]:text-sidebar-accent-foreground dark:from-sidebar-accent dark:to-sidebar-accent';

export interface ShellPanelItemProps {
	to: PanelLinkTo;
	params?: LinkProps['params'];
	search?: LinkProps['search'];
	activeOptions?: LinkProps['activeOptions'];
	// Forces the active state, overriding TanStack's path/search match. Use when two items share a
	// path and only a caller-derived rule (e.g. the current tab) decides which one highlights.
	active?: boolean;
	icon?: ComponentType<RailItemIconProps>;
	leading?: ReactNode;
	children: ReactNode;
	trailing?: ReactNode;
	disabled?: boolean;
	// Dims the row and marks it with a padlock when its page is behind an add-on the tenant has not
	// bought. Navigation still works: the page it lands on is the purchase wall.
	locked?: boolean;
	// Hides the item unless the user holds at least one of these. Omit to always show.
	anyPermission?: readonly PermissionKey[];
	'data-testid'?: string;
}

export function ShellPanelItem({
	to,
	params,
	search,
	activeOptions,
	active,
	icon: Icon,
	leading,
	children,
	trailing,
	disabled,
	locked,
	anyPermission,
	'data-testid': testId,
}: ShellPanelItemProps) {
	const allowed = useHasAnyPermission(anyPermission ?? []);
	const forced = active !== undefined;
	if (anyPermission && anyPermission.length > 0 && !allowed) return null;
	return (
		<Link
			to={to}
			params={params}
			search={search}
			activeOptions={activeOptions}
			data-testid={testId}
			data-active={forced && active ? 'true' : undefined}
			aria-disabled={disabled || undefined}
			tabIndex={disabled ? -1 : undefined}
			data-locked={locked ? 'true' : undefined}
			className={cn(PANEL_ITEM_BASE, locked && PANEL_ITEM_LOCKED, forced && active && PANEL_ITEM_ACTIVE)}
			activeProps={forced ? undefined : { className: PANEL_ITEM_ACTIVE, 'data-active': 'true' }}
		>
			{Icon ? <Icon className="size-4 shrink-0" /> : leading}
			<span className="truncate">{children}</span>
			{trailing ? (
				<span className="ml-auto flex shrink-0 items-center text-xs font-medium">{trailing}</span>
			) : locked ? (
				<Lock className="ml-auto size-3 shrink-0 opacity-50" aria-hidden />
			) : null}
		</Link>
	);
}
